import { isPlatformBrowser } from '@angular/common';
import { FAILED_RECORDINGS_DIALOG_CONFIG } from '../../config/floating-ai-dialog.config';
import { ChatService } from '../../../chat/chat.service';
import { Component, EventEmitter, HostListener, Inject, Input, Output, PLATFORM_ID, ViewChild } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, map, Observable, startWith, takeUntil } from 'rxjs';
import { GroupService } from '../../../core/services/group.service';
import { UserService } from '../../../core/services/user.service';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { NotificationService } from './../../../core/services/notification.service';
import { PatientService } from './../../../core/services/patient.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RecordingStorageService } from '../../../core/services/recording-storage.service';
import { PracticeSwitchService } from '../../../core/services/practice-switch.service';
import { FailedRecordingsDialogComponent } from '../failed-recordings-dialog/failed-recordings-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Announcement, AnnouncementService, AnnouncementType } from '../../../core/services/announcement.service';
import { AnnouncementDialogComponent } from '../announcement-dialog/announcement-dialog.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent extends BaseComponent {
  isSticky: boolean = false;
  showBackToTop: boolean = false;
  isBrowser: boolean;
  @Input() isMobile!: boolean;
  @Output() toggleSidenav = new EventEmitter<void>();
  @ViewChild('groupMenuTrigger') groupMenuTrigger!: MatMenuTrigger;

  groups: any[] = [];
  groupSearchControl = new FormControl<string>('', { nonNullable: true });
  filteredGroups$!: Observable<any[]>;
  private groupsList$ = new BehaviorSubject<any[]>([]);
  selectedGroup: any;
  groupedNotifications: any[] = [];
  totalNotifications = 0;
  showDemoMessage = false;

  /**
   * The megaphone archive and its unread count. Kept as observables and bound with `| async` so
   * there is no subscription to leak, matching how `filteredGroups$` is handled above.
   */
  announcementArchive$!: Observable<Announcement[]>;
  announcementUnread$!: Observable<number>;

  demoMessage: string = 'This account is for demonstration purposes only. Create your account at <a href="https://doctor.medicai.co.za" target="_blank">https://doctor.medicai.co.za</a>';
  failedRecordingsCount: number = 0;
  failedRecordingsDialogShown: boolean = false;

  aiDialogOpen: boolean = false;


  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private userService: UserService,
    private groupService: GroupService,
    private router: Router,
    private notificationService: NotificationService,
    private patientService: PatientService,
    private snack: MatSnackBar,
    fs: FeedbackService,
    private md: MatDialog,
    private announcementService: AnnouncementService,
    readonly chatService: ChatService,
    private practiceSwitch: PracticeSwitchService,
    private recordingStorageService?: RecordingStorageService // Optional - non-breaking
  ) {
    super(fs, md);

    this.announcementArchive$ = this.announcementService.archive$;
    this.announcementUnread$ = this.announcementService.unreadCount$;

    this.isBrowser = isPlatformBrowser(this.platformId);
    // Chat (messaging hub, tasks/messaging-hub-architecture.md M1): the header exists only once signed in, so this is
    // where the hub connection starts. It stays off if this platform has chat switched off (the token route 404s).
    if (this.isBrowser) void this.chatService.ensureStarted();
    // On Capacitor-native builds the header is always pinned — the app shell
    // pattern keeps the top bar visible at all times, independent of scroll.
    if (Capacitor.isNativePlatform()) {
      this.isSticky = true;
    }

    if (window.location.href.indexOf('demo.medicai.co.za') !== -1) {
      this.showDemoMessage = true;
    }

    this.filteredGroups$ = combineLatest([
      this.groupSearchControl.valueChanges.pipe(startWith(this.groupSearchControl.value)),
      this.groupsList$
    ]).pipe(
      map(([text, groups]) => {
        const list = groups ?? [];
        const t = (text ?? '').toLowerCase().trim();
        if (!t) {
          return list;
        }
        return list.filter((g: any) => (g.name ?? '').toLowerCase().includes(t));
      })
    );

    this.groupService.userGroups$
      .pipe(
        takeUntil(this.destroy$),
        map(groups =>
          // sort _descending_ by last name
          groups?.sort((a: { name: string; }, b: { name: string; }) => {
            const getLast = (full: string) =>
              full
                .trim()
                .split(/\s+/)
                .pop()!
                .toLowerCase();

            const aLast = getLast(a.name);
            const bLast = getLast(b.name);

            // for descending: b vs a
            return aLast.localeCompare(bLast);
          })
        )
      )
      .subscribe(sortedGroups => {
        this.groups = sortedGroups ?? [];
        this.groupsList$.next(this.groups);
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        this.selectedGroup = group;
      });

    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notifications => {
        this.groupedNotifications = this.groupAndSortNotifications(notifications);
        this.totalNotifications = this.groupedNotifications.length;
      });

    this.patientService.aiDialogOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOpen => {
        this.aiDialogOpen = isOpen;
      });

    // ✅ Subscribe to failed recordings observable (replaces polling)
    this.subscribeToFailedRecordingsCount();
  }

  onGroupMenuOpened(): void {
    this.groupSearchControl.setValue('', { emitEvent: true });
  }

  /**
   * Keeps keys typed in the search box away from the menu. Otherwise mat-menu's typeahead moves
   * focus to the first item starting with the typed letter, and Space selects it. Escape still
   * reaches the menu so it closes.
   */
  onGroupSearchKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  }

  clearGroupSearch(event: Event): void {
    event.stopPropagation();
    this.groupSearchControl.setValue('');
  }

  selectGroupFromMenu(group: any): void {
    this.setSelectedGroup(group);
    this.groupMenuTrigger?.closeMenu();
  }

  private subscribeToFailedRecordingsCount() {
    if (!this.recordingStorageService || !this.isBrowser) return;

    // Subscribe to failed recordings observable - automatically updates when recordings change
    this.recordingStorageService.failedRecordings$
      .pipe(takeUntil(this.destroy$))
      .subscribe(recordings => {
        this.failedRecordingsCount = recordings.length;
        // Disabled: auto-prompt for failed recordings was interrupting users during their day.
        // Users can still open the dialog manually from the header.
        // this.maybeShowFailedRecordingsFound();
      });
  }

  private async maybeShowFailedRecordingsFound() {
    if (this.failedRecordingsCount > 0 && !this.failedRecordingsDialogShown) {
      setTimeout(async () => {
        // Don't show if dialog already shown or if AI dialog is open
        if (this.failedRecordingsDialogShown) {
          return;
        }

        // Check if AI dialog is open - don't show if it is
        if (this.aiDialogOpen) {  
          return;
        }

        const confirmed = await this.showConfirmationDialog('Stored Recordings Found', 'We detected recordings stored locally on this device. This is likely the result of a failed recording. We suggest that you download them for reprocessing and clear them from the local storage cache to ensure there is sufficient space to store recordings in the future. Would you like to view them now?', [], 'Close', 'View Recordings');
        if (confirmed) {
          this.openFailedRecordingsDialog();
        }
      }, 3000);
    }
  }


  openFailedRecordingsDialog() {

    this.failedRecordingsDialogShown = true;
    const dialogRef = this.dialog.open(FailedRecordingsDialogComponent, FAILED_RECORDINGS_DIALOG_CONFIG);

    const sub = dialogRef.afterClosed().subscribe(() => {
      this.failedRecordingsDialogShown = false;
      if (sub) {
        sub.unsubscribe();
      }
    });

    // Observable will automatically update count when recordings change
    // No need to manually refresh after dialog closes
  }

  private groupAndSortNotifications(notifications: any[]) {
    if (!notifications?.length) {
      return [];
    }

    // Group notifications by the socketId which contains the name of the run or the patient name
    const grouped = _.groupBy(notifications, notification => notification.socketId);

    // Sort the notifications within each group by createdAt in descending order
    const sortedGroups = _.mapValues(grouped, group => {
      return _.orderBy(group, 'createdAt', 'desc');
    });

    // Transform the sorted groups into an array and select the most recent notification from each group
    const result = _.map(sortedGroups, (group, name) => {
      return {
        name,
        notification: group[0]
      };
    });

    // Sort the groups by the createdAt of the most recent notification in descending order
    return _.orderBy(result, group => group.notification.createdAt, 'desc');
  }

  setSelectedGroup(group: any) {
    const changing = !!this.selectedGroup && group.id !== this.selectedGroup.id;
    this.practiceSwitch.switchTo(group).then(switched => {
      if (switched && changing) {
        this.router.navigate(['/patients', 'schedule']);
      }
    });
  }

  /**
   * Opens one archived announcement as a single-page read.
   *
   * Reading from the megaphone records a `seen`, which is what keeps the unread badge honest — but
   * it never un-dismisses anything: the server refuses to downgrade a `dismissed` or
   * `acknowledged` receipt back to `seen`.
   */
  openAnnouncement(announcement: Announcement): void {
    this.md.open(AnnouncementDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      autoFocus: false,
      data: { announcements: [announcement] },
    });
  }

  /** Same icon vocabulary as the dialog and the strip, so one thing looks like itself everywhere. */
  announcementIcon(type: AnnouncementType): string {
    switch (type) {
      case 'critical': return 'gpp_maybe';
      case 'release': return 'auto_awesome';
      default: return 'info';
    }
  }

  onSettingsClick() {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    this.router.navigate(['/settings']);
  }

  async onLogout() {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    const confirmed = await this.feedbackService.showConfirmation(
      'Confirm Logout',
      'Are you sure you want to log out? Unsaved changes will be lost.',
      [],
      'Cancel',
      'Log Out'
    );
    if (!confirmed) return;

    // One navigation to a fresh /login — see `UserService.logoutAndRedirect`. The dashboard's
    // `beforeunload` guard stands down for a deliberate sign-out; the recording guard in
    // `ai-dialog` does not, which is why the recording check above still runs first.
    this.userService.logoutAndRedirect();
  }

  handleNotificationClick(notification: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    this.notificationService.markNotificationAsRead(notification.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    if (notification.patientId) {
      this.patientService.getPatientById(notification.patientId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['file', notification.patientId],
            }
          }]);
        }, error => {
          console.error(error);
          this.showToastMessage('There was an error loading the patient file. Please try again.');
        });
    } else {
      this.router.navigate(['/request', notification.aiRequestId]);
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.isBrowser) {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      // Keep the native header permanently sticky; only toggle on the web.
      if (!Capacitor.isNativePlatform()) {
        this.isSticky = scrollPosition > 0;
      }
      this.showBackToTop = scrollPosition > 200;
    }
  }

  scrollToTop() {
    if (this.isBrowser) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
